import React, { useEffect, useState } from "react";
import { Download, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatINR, downloadBlob } from "@/lib/api";
import { toast } from "sonner";

export const OutstandingReportTab = ({ shops, salesmen }) => {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [shopFilter, setShopFilter] = useState("all");
  const [salesmanFilter, setSalesmanFilter] = useState("all");

  const load = () => {
    const params = {};
    if (shopFilter !== "all") params.shop_id = shopFilter;
    if (salesmanFilter !== "all") params.salesman = salesmanFilter;
    api.get("/crm/reports/outstanding", { params }).then(({ data }) => { setRows(data.rows); setTotal(data.total_outstanding); })
      .catch(() => { setRows([]); setTotal(0); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shopFilter, salesmanFilter]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (shopFilter !== "all") params.set("shop_id", shopFilter);
    if (salesmanFilter !== "all") params.set("salesman", salesmanFilter);
    downloadBlob(`/crm/reports/outstanding.csv?${params.toString()}`, "Outstanding-Report.csv").catch(() => toast.error("Export failed"));
  };

  return (
    <div data-testid="outstanding-report-tab">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger data-testid="outstanding-shop-filter" className="w-44"><SelectValue placeholder="All Customers" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Customers</SelectItem>{shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.shop_name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={salesmanFilter} onValueChange={setSalesmanFilter}>
            <SelectTrigger data-testid="outstanding-salesman-filter" className="w-44"><SelectValue placeholder="All Salesmen" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Salesmen</SelectItem>{salesmen.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="outstanding-export-csv" size="sm" variant="outline" className="gap-1.5" onClick={exportCsv}><Download className="h-4 w-4" /> Export CSV</Button>
      </div>

      <div className="rounded-lg bg-[#F7F0E6] border border-border p-3 mb-3 flex items-center justify-between">
        <span className="text-sm">Total Outstanding</span>
        <span data-testid="total-outstanding-amount" className="font-mono text-lg text-[hsl(var(--primary))]">{formatINR(total)}</span>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="outstanding-report-table">
            <TableHeader><TableRow className="bg-[#F4EBDD]">
              <TableHead>Customer</TableHead><TableHead>Invoice</TableHead><TableHead>Invoice Date</TableHead>
              <TableHead>Due Date</TableHead><TableHead className="text-right">Invoice Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Outstanding</TableHead>
              <TableHead className="text-right">Days Overdue</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!rows ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No outstanding balances.</TableCell></TableRow>
              ) : rows.map((r, i) => (
                <TableRow key={`${r.invoice_no}-${i}`} className="hover:bg-[#F7F0E6]">
                  <TableCell className="text-sm">{r.shop_name}</TableCell>
                  <TableCell className="font-mono text-sm">{r.invoice_no}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.invoice_date || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.due_date || "-"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatINR(r.invoice_amount)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-[#1F5B2E]">{formatINR(r.paid)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-[#7A1F1F]">{formatINR(r.outstanding)}</TableCell>
                  <TableCell className="text-right">
                    {r.days_overdue > 0 ? (
                      <Badge data-testid="days-overdue-badge" className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9] gap-1"><AlertTriangle className="h-3 w-3" /> {r.days_overdue}d</Badge>
                    ) : <span className="text-muted-foreground text-xs">-</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
